import * as React from 'react'
import { cn } from '../../../lib/cn'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../Tooltip'

export interface IdentifierChannelOption {
  /** Wire name — the value stored in `allowed_identifier_channels`. */
  name: string
  /** Human-readable label shown on the chip. */
  label: string
}

export interface IdentifierChannelPickerProps {
  /** All identifier-type fields from the tenant's active schema. */
  options: IdentifierChannelOption[]
  /** Selected channel names (subset of options[].name). */
  value: string[]
  onChange: (next: string[]) => void
  /**
   * Highlight a specific channel chip when the publish-validator returns
   * FLOW_REF_UNKNOWN_IDENTIFIER. Pass the offending channel name (the
   * value in `nodes[i].config.allowed_identifier_channels[j]`).
   *
   * Accepts either a single channel name (legacy) or an array of names.
   */
  errorFor?: string | string[]
  /**
   * Channel names that are stored in the flow rules but are no longer
   * enabled at the tenant level. Each renders as a dashed-ring warning
   * chip with an X button that removes it from the `value` array via
   * `onChange`. The tooltip explains what is wrong and where to fix it.
   */
  tenantDisabledValues?: string[]
  disabled?: boolean
  className?: string
}

/**
 * IdentifierChannelPicker — multi-select chip row for identifier channels.
 *
 * Reads from the tenant's active identity schema (identifier-type fields
 * only). Each chip toggles its channel into/out of the `value` array.
 *
 * Accessibility contract:
 * - Each chip is a `<button>` with `aria-pressed` indicating selection.
 * - The error chip receives `aria-invalid` and an `aria-describedby`
 *   pointing at the error message.
 * - Tenant-disabled chips render with aria-describedby pointing at a
 *   sr-only description and an accessible X button for removal.
 * - The entire group is labelled by a visually-hidden `<fieldset>` legend.
 */
export const IdentifierChannelPicker = React.forwardRef<
  HTMLDivElement,
  IdentifierChannelPickerProps
>(
  (
    {
      options,
      value,
      onChange,
      errorFor,
      tenantDisabledValues,
      disabled = false,
      className,
    },
    ref,
  ) => {
    // Normalise errorFor to a Set for O(1) lookup regardless of single vs array.
    const errorSet = React.useMemo<Set<string>>(() => {
      if (!errorFor) return new Set()
      return new Set(Array.isArray(errorFor) ? errorFor : [errorFor])
    }, [errorFor])

    const disabledSet = React.useMemo<Set<string>>(
      () => new Set(tenantDisabledValues ?? []),
      [tenantDisabledValues],
    )

    if (options.length === 0 && disabledSet.size === 0) {
      return (
        <div
          ref={ref}
          className={cn(
            'rounded-md border border-ia-border bg-ia-elevated px-3 py-2 text-[11px] text-ia-text-muted',
            className,
          )}
          role="status"
          aria-live="polite"
        >
          No identifier fields configured. Visit Identity &amp; Sign-in to add one.
        </div>
      )
    }

    const toggle = (name: string) => {
      if (disabled) return
      const next = value.includes(name) ? value.filter((v) => v !== name) : [...value, name]
      onChange(next)
    }

    const removeDisabled = (name: string, e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation()
      onChange(value.filter((v) => v !== name))
    }

    return (
      <TooltipProvider>
        <div ref={ref} className={cn('flex flex-wrap gap-1.5', className)}>
          {options.map((opt) => {
            const isSelected = value.includes(opt.name)
            const hasError = errorSet.has(opt.name)
            const errorId = hasError ? `icp-error-${opt.name}` : undefined

            return (
              <React.Fragment key={opt.name}>
                <button
                  type="button"
                  aria-pressed={isSelected}
                  aria-invalid={hasError ? true : undefined}
                  aria-describedby={errorId}
                  disabled={disabled}
                  onClick={() => toggle(opt.name)}
                  className={cn(
                    'rounded-pill border px-2.5 py-1 text-xs transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
                    'focus-visible:ring-ia-border-focus',
                    disabled && 'cursor-not-allowed opacity-50',
                    hasError
                      ? 'border-ia-error bg-ia-error/10 text-ia-error'
                      : isSelected
                        ? 'border-ia-success/40 bg-ia-success-bg text-ia-success hover:bg-ia-success/25'
                        : 'border-ia-border bg-transparent text-muted-foreground hover:border-ia-border-strong hover:text-foreground',
                  )}
                >
                  {opt.label}
                </button>
                {hasError && (
                  <span id={errorId} className="sr-only">
                    Unknown identifier channel &ldquo;{opt.name}&rdquo; — not in the active
                    identity schema.
                  </span>
                )}
              </React.Fragment>
            )
          })}

          {/* Tenant-disabled warning chips — values stored in rules but no longer
              enabled at the tenant level. Rendered after the normal chips so they
              don't interrupt the primary selection UI. Each chip has a dashed-ring
              warning style and an X button to remove it from the rules. */}
          {Array.from(disabledSet).map((name) => {
            const descId = `icp-disabled-${name}`
            return (
              <Tooltip key={`disabled-${name}`}>
                <TooltipTrigger asChild>
                  <span
                    role="group"
                    aria-label={`${name} — tenant disabled`}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-pill',
                      'border border-dashed border-ia-error bg-ia-error/10',
                      'ring-1 ring-ia-error',
                      'px-2 py-0.5 text-xs text-ia-error',
                    )}
                  >
                    <span aria-describedby={descId}>{name}</span>
                    <span id={descId} className="sr-only">
                      This identifier channel is no longer enabled at the tenant level. Click X to
                      remove it, or re-enable it on the Identity &amp; Sign-in page first.
                    </span>
                    {!disabled && (
                      <button
                        type="button"
                        aria-label={`Remove disabled channel ${name}`}
                        onClick={(e) => removeDisabled(name, e)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') removeDisabled(name, e)
                        }}
                        className={cn(
                          'ml-0.5 rounded-sm text-ia-error transition-colors',
                          'hover:text-ia-error/70 focus-visible:outline-none',
                          'focus-visible:ring-1 focus-visible:ring-ia-border-focus',
                        )}
                      >
                        <svg
                          aria-hidden="true"
                          viewBox="0 0 12 12"
                          fill="currentColor"
                          className="h-3 w-3"
                        >
                          <path d="M2.22 2.22a.75.75 0 0 1 1.06 0L6 4.94l2.72-2.72a.75.75 0 1 1 1.06 1.06L7.06 6l2.72 2.72a.75.75 0 1 1-1.06 1.06L6 7.06 3.28 9.78a.75.75 0 0 1-1.06-1.06L4.94 6 2.22 3.28a.75.75 0 0 1 0-1.06Z" />
                        </svg>
                      </button>
                    )}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-center">
                  This identifier channel is no longer enabled at the tenant level. Click X to
                  remove it, or re-enable it on the{' '}
                  <strong>Identity &amp; Sign-in</strong> page first.
                </TooltipContent>
              </Tooltip>
            )
          })}
        </div>
      </TooltipProvider>
    )
  },
)

IdentifierChannelPicker.displayName = 'IdentifierChannelPicker'
